var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { EventEmitter, DataRow, utils, i18n, DataType } from '@easydata/core';
import { eqDragManager, DropEffect } from '../utils/drag_manager';
import { domel } from '../utils/dom_elem_builder';
import { getElementAbsolutePos, eqCssPrefix } from '../utils/ui-utils';
import { AutoResizeColumns } from './easy_grid_options';
import { GridColumnList, GridColumnAlign } from './easy_grid_columns';
import { GridCellRendererStore } from './easy_grid_cell_renderer';
var DEFAULT_ROW_HEIGHT = 36;
var DEFAULT_ROW_COUNT = 15;
/** Represents a grid widget with columns rows, paging, custom rendering and more */
var EasyGrid = /** @class */ (function () {
    /** Creates and initializes all internal properties of the grid object */
    function EasyGrid(options) {
        this.cssPrefix = "keg";
        this.pagination = { page: 1, pageSize: 30, total: 0 };
        this.paginationOptions = {
            maxButtonCount: 10,
            useBootstrap: false //true
        };
        this.defaultDataGridOptions = {
            slot: null,
            dataTable: null,
            fixHeightOnFirstRender: false,
            syncGridColumns: true,
            useRowNumeration: true,
            allowDragDrop: false,
            aggregates: {
                settings: null,
                calculator: null
            },
            paging: {
                enabled: true,
                pageSize: 30,
                pageSizeItems: [20, 30, 50, 100, 200]
            },
            columnWidths: {
                autoResize: AutoResizeColumns.Always,
                stringColumns: {
                    min: 100,
                    max: 500,
                    default: 250
                },
                numberColumns: {
                    min: 60,
                    default: 120
                },
                boolColumns: {
                    min: 50,
                    default: 80
                },
                dateColumns: {
                    min: 80,
                    default: 200
                },
                otherColumns: {
                    min: 100,
                    max: 500,
                    default: 250
                },
                rowNumColumn: {
                    min: 40,
                    default: 60
                }
            },
            showPlusButton: false,
            viewportRowsCount: null,
            showActiveRow: true
        };
        this.rowsOnPagePromise = null;
        this.containerInitialHeight = 0;
        this.firstRender = true;
        this.prevRowTotals = null;
        this.landingIndex = -1;
        this.landingSlot = domel('div')
            .addClass(this.cssPrefix + "-col-landing-slot")
            .addChildElement(domel('div')
            .toDOM())
            .toDOM();
        this._activeRowIndex = -1;
        if (options && options.paging) {
            options.paging = utils.assign(this.defaultDataGridOptions.paging, options.paging);
        }
        this.options = this.mergeOptions(options);
        this.processColumnWidthsOptions();
        if (!this.options.slot)
            throw Error('"slot" parameter is required to initialize EasyDataGrid');
        if (!this.options.dataTable)
            throw Error('"dataTable" parameter is required to initialize EasyDataGrid');
        this.dataTable = options.dataTable;
        this.eventEmitter = new EventEmitter(this);
        this.cellRendererStore = new GridCellRendererStore(options);
        this.columns = new GridColumnList(this.dataTable.columns, this);
        this.setSlot(this.options.slot);
        this.init(this.options);
    }
    EasyGrid.prototype.mergeOptions = function (options) {
        var colWidthOptions = utils.assignDeep({}, this.defaultDataGridOptions.columnWidths, options.columnWidths);
        var pagingOptions = utils.assignDeep({}, this.defaultDataGridOptions.paging, options.paging);
        var result = utils.assign({}, this.defaultDataGridOptions, options);
        result.columnWidths = colWidthOptions;
        result.paging = pagingOptions;
        return result;
    };
    EasyGrid.prototype.processColumnWidthsOptions = function () {
        var widthOptions = this.options.columnWidths;
        if (!widthOptions)
            return;
        //string columns
        utils.getStringDataTypes().forEach(function (dataType) {
            widthOptions[dataType] = __assign(__assign({}, widthOptions.stringColumns), widthOptions[dataType]);
        });
        //numeric columns
        utils.getNumericDataTypes().forEach(function (dataType) {
            widthOptions[dataType] = __assign(__assign({}, widthOptions.numberColumns), widthOptions[dataType]);
        });
        //bool columns
        widthOptions[DataType.Bool] = __assign(__assign({}, widthOptions.boolColumns), widthOptions[DataType.Bool]);
        //date columns
        utils.getDateDataTypes().forEach(function (dataType) {
            widthOptions[dataType] = __assign(__assign({}, widthOptions.dateColumns), widthOptions[dataType]);
        });
        //other columns
        var knownTypes = __spreadArrays(utils.getStringDataTypes(), utils.getNumericDataTypes(), utils.getDateDataTypes(), [
            DataType.Bool
        ]);
        utils.getAllDataTypes().forEach(function (dataType) {
            if (!(dataType in knownTypes)) {
                widthOptions[dataType] = __assign(__assign({}, widthOptions.otherColumns), widthOptions[dataType]);
            }
        });
        widthOptions[DataType.Unknown] = widthOptions.otherColumns;
    };
    EasyGrid.prototype.setSlot = function (slot) {
        if (typeof slot === 'string') {
            if (slot.length) {
                if (slot[0] === '#') {
                    this.slot = document.getElementById(slot.substring(1));
                }
                else if (slot[0] === '.') {
                    var result = document.getElementsByClassName(slot.substring(1));
                    if (result.length)
                        this.slot = result[0];
                }
                else {
                    throw Error('Unrecognized slot parameter ' +
                        '(Must be id, class or HTMLElement): ' + slot);
                }
            }
        }
        else {
            this.slot = slot;
        }
    };
    /** Initializes grid widget according to the options passed in the parameter */
    EasyGrid.prototype.init = function (options) {
        var _this = this;
        if (options.onInit) {
            this.addEventListener('init', options.onInit);
        }
        if (options.onRowClick) {
            this.addEventListener('rowClick', options.onRowClick);
        }
        if (options.onRowDbClick) {
            this.addEventListener('rowDbClick', options.onRowDbClick);
        }
        if (options.onPlusButtonClick) {
            this.addEventListener('plusButtonClick', options.onPlusButtonClick);
        }
        if (options.onColumnChanged) {
            this.addEventListener('columnChanged', options.onColumnChanged);
        }
        if (options.onColumnDeleted) {
            this.addEventListener('columnDeleted', options.onColumnDeleted);
        }
        if (options.onColumnMoved) {
            this.addEventListener('columnMoved', options.onColumnMoved);
        }
        if (options.onPageChanged) {
            this.addEventListener('pageChanged', options.onPageChanged);
        }
        if (options.onActiveRowChanged) {
            this.addEventListener('activeRowChanged', options.onActiveRowChanged);
        }
        this.addEventListener('pageChanged', function (ev) { return _this.activeRowIndex = -1; });
        utils.assignDeep(this.paginationOptions, options.pagination);
        this.pagination.pageSize = this.options.paging.pageSize
            || this.pagination.pageSize;
        if (this.options.allowDragDrop) {
            eqDragManager.registerDropContainer({
                element: this.slot,
                scopes: ["gridColumnMove"],
                onDragEnter: function (_, ev) {
                    _this.slot.classList.add(eqCssPrefix + "-drophover");
                    _this.showLandingSlot(ev.pageX, ev.pageY);
                },
                onDragOver: function (_, ev) {
                    _this.showLandingSlot(ev.pageX, ev.pageY);
                },
                onDragLeave: function (_, ev) {
                    ev.dropEffect = DropEffect.Forbid;
                    _this.slot.classList.remove(eqCssPrefix + "-drophover");
                    _this.hideLandingSlot();
                },
                onDrop: function (_, ev) {
                    _this.dataTable.columns.move(ev.data.column, _this.landingIndex);
                    _this.refresh();
                    _this.fireEvent({
                        type: 'columnMoved',
                        columnId: ev.data.column.id,
                        newIndex: _this.landingIndex
                    });
                }
            });
        }
        this.refresh();
        this.fireEvent('init');
    };
    /** Fires a grid event. You can pass either an event type
     * (like 'init', 'rowClick', 'pageChanged', etc )
     * or a ready-to-use grid event object
     * */
    EasyGrid.prototype.fireEvent = function (event) {
        if (typeof event === "string") {
            this.eventEmitter.fire(event);
        }
        else {
            this.eventEmitter.fire(event.type, event);
        }
    };
    /** Allows to set the data (represented by a EasyDataTable object)
     *  or to replace the existing one associated with the grid */
    EasyGrid.prototype.setData = function (data) {
        this.dataTable = data;
        this.clear();
        this.refresh();
    };
    /** Returns the EasyDataTable object associated with the grid via `setData()` call */
    EasyGrid.prototype.getData = function () {
        return this.dataTable;
    };
    /** Gets the list of grid columns */
    EasyGrid.prototype.getColumns = function () {
        return this.columns;
    };
    /** This function is called when the grid is destroyed */
    EasyGrid.prototype.destroy = function () {
        this.slot.innerHTML = "";
    };
    /** Clears the current DOM object and re-renders everything from the scratch */
    EasyGrid.prototype.refresh = function () {
        this.clearDOM();
        this.render();
    };
    EasyGrid.prototype.clearDOM = function () {
        this.slot.innerHTML = '';
    };
    /** Clears all DOM object in the grid and return it to its initial state */
    EasyGrid.prototype.clear = function () {
        this.pagination.page = 1;
        this.clearDOM();
    };
    /** Renders the grid */
    EasyGrid.prototype.render = function () {
        var _this = this;
        if (!this.hasData() && !this.options.showPlusButton)
            return;
        this.containerInitialHeight = this.slot.clientHeight;
        this.rootDiv = document.createElement('div');
        this.rootDiv.style.width = '100%';
        this.rootDiv.classList.add(this.cssPrefix + "-root");
        this.columns.sync(this.dataTable.columns, this.options.useRowNumeration);
        this.renderHeader();
        this.rootDiv.appendChild(this.headerDiv);
        this.renderBody();
        this.rootDiv.appendChild(this.bodyDiv);
        this.renderFooter();
        this.rootDiv.appendChild(this.footerDiv);
        var gridContainer = document.createElement('div');
        gridContainer.classList.add(this.cssPrefix + "-container");
        gridContainer.appendChild(this.rootDiv);
        this.slot.appendChild(gridContainer);
        var needAutoResize = this.options.columnWidths.autoResize !== AutoResizeColumns.Never;
        if (this.rowsOnPagePromise) {
            this.rowsOnPagePromise
                .then(function () { return _this.updateHeight(); })
                .then(function () {
                _this.firstRender = false;
                _this.rowsOnPagePromise = null;
            });
        }
        else {
            setTimeout(function () {
                _this.updateHeight()
                    .then(function () {
                    _this.firstRender = false;
                    if (needAutoResize) {
                        _this.resizeColumns();
                    }
                });
            }, 100);
        }
    };
    EasyGrid.prototype.updateHeight = function () {
        var _this = this;
        return new Promise(function (resolve) {
            if (_this.options.viewportRowsCount) {
                var firstRow = _this.bodyCellContainerDiv.firstElementChild;
                var rowHeight = firstRow ? firstRow.offsetHeight : DEFAULT_ROW_HEIGHT;
                var rowCount = _this.options.viewportRowsCount; // || DEFAULT_ROW_COUNT;
                var viewportHeight_1 = rowHeight * rowCount;
                domel(_this.bodyViewportDiv)
                    .setStyle('height', viewportHeight_1 + "px");
                setTimeout(function () {
                    var sbHeight = _this.bodyViewportDiv.offsetHeight - _this.bodyViewportDiv.clientHeight;
                    viewportHeight_1 = viewportHeight_1 + sbHeight;
                    domel(_this.bodyViewportDiv)
                        .setStyle('height', viewportHeight_1 + "px");
                    resolve();
                }, 100);
                return;
            }
            else if (_this.containerInitialHeight > 0) {
                // comment for now since it does not work in all cases
                // const bodyHeight = this.containerInitialHeight - this.headerDiv.offsetHeight - this.footerDiv.offsetHeight;
                // domel(this.bodyDiv)
                //     .setStyle('height', `${bodyHeight}px`);
            }
            resolve();
        })
            .then(function () {
            if (_this.options.fixHeightOnFirstRender && _this.firstRender) {
                _this.slot.style.height = _this.slot.offsetHeight + "px";
            }
        });
    };
    EasyGrid.prototype.getContainerWidth = function () {
        return this.columns.getItems()
            .filter(function (col) { return col.isVisible; })
            .map(function (col) { return col.width; })
            .reduce(function (sum, current) { return sum + current; });
    };
    EasyGrid.prototype.renderHeader = function () {
        var _this = this;
        this.headerDiv = domel('div')
            .addClass(this.cssPrefix + "-header")
            .toDOM();
        this.headerViewportDiv = domel('div', this.headerDiv)
            .addClass(this.cssPrefix + "-header-viewport")
            .toDOM();
        this.headerCellContainerDiv = domel('div', this.headerViewportDiv)
            .addClass(this.cssPrefix + "-header-cell-container")
            .toDOM();
        this.headerRowDiv = domel('div', this.headerCellContainerDiv)
            .addClass(this.cssPrefix + "-header-row")
            .toDOM();
        this.columns.getItems().forEach(function (column, index) {
            if (!column.isVisible) {
                return;
            }
            var hd = _this.renderColumnHeader(column, index);
            _this.headerRowDiv.appendChild(hd);
            if (column.isRowNum) {
                domel(hd)
                    .addChildElement(_this.renderHeaderButtons());
            }
        });
        var containerWidth = this.getContainerWidth();
        domel(this.headerCellContainerDiv)
            .setStyle('width', containerWidth + "px");
    };
    EasyGrid.prototype.hasData = function () {
        return this.dataTable.columns.count > 0;
    };
    EasyGrid.prototype.renderColumnHeader = function (column, index) {
        var _this = this;
        var colBuilder = domel('div')
            .addClass(this.cssPrefix + "-header-cell")
            .data('col-idx', "" + index)
            .setStyle('width', column.width + "px");
        if (column.dataColumn) {
            colBuilder
                .data('col-id', "" + column.dataColumn.id);
        }
        var colDiv = colBuilder.toDOM();
        domel('div', colDiv)
            .addClass(this.cssPrefix + "-header-cell-resize");
        if (!column.isRowNum) {
            domel('div', colDiv)
                .addClass(this.cssPrefix + "-header-cell-label")
                .text(column.label);
        }
        if (column.description) {
            domel('div', colDiv)
                .addClass('question-mark')
                .title(column.description);
        }
        if (this.options.allowDragDrop) {
            eqDragManager.registerDraggableItem({
                element: colDiv,
                scope: "gridColumnMove",
                data: { column: column },
                renderer: function (dragImage) {
                    dragImage.innerHTML = '';
                    var attrLabel = document.createElement('div');
                    attrLabel.innerText = column.label;
                    dragImage.classList.add(_this.cssPrefix + "-sortable-helper");
                    dragImage.appendChild(attrLabel);
                },
                onDragStart: function (ev) {
                    ev.dropEffect = DropEffect.Allow;
                }
            });
        }
        return colDiv;
    };
    EasyGrid.prototype.renderBody = function () {
        var _this = this;
        this.bodyDiv = domel('div')
            .addClass(this.cssPrefix + "-body")
            .toDOM();
        this.bodyViewportDiv = domel('div', this.bodyDiv)
            .addClass(this.cssPrefix + "-body-viewport")
            .attr('tabIndex', '0')
            .toDOM();
        this.bodyCellContainerDiv = domel('div', this.bodyViewportDiv)
            .addClass(this.cssPrefix + "-cell-container")
            .toDOM();
        var showAggrs = this.canShowAggregates();
        if (this.dataTable) {
            this.showProgress();
            this.rowsOnPagePromise = this.getRowsToRender()
                .then(function (rows) {
                _this.pagination.total = _this.dataTable.getTotal();
                _this.hideProgress();
                //prevent double rendering (bad solution, we have to figure out how to avoid this behavior properly)
                _this.bodyCellContainerDiv.innerHTML = '';
                _this.prevRowTotals = null;
                var rowsToRender = 0;
                if (rows.length) {
                    var groups_1 = showAggrs
                        ? _this.options.aggregates.settings.getGroups()
                        : [];
                    rowsToRender = (rows.length < _this.pagination.pageSize)
                        ? rows.length
                        : _this.pagination.pageSize;
                    rows.forEach(function (row, index) {
                        if (showAggrs)
                            _this.updateTotalsState(groups_1, row);
                        //we don't actually render the last row
                        if (index < rowsToRender) {
                            var tr = _this.renderRow(row, index);
                            _this.bodyCellContainerDiv.appendChild(tr);
                        }
                    });
                    var showGrandTotalsOnEachPage = _this.options.aggregates && _this.options.aggregates.showGrandTotalsOnEachPage;
                    if (showAggrs && (_this.isLastPage() || showGrandTotalsOnEachPage)) {
                        var row = new DataRow(_this.dataTable.columns, new Array(_this.dataTable.columns.count));
                        _this.updateTotalsState(groups_1, row, true);
                    }
                }
                var needAutoResize = _this.options.columnWidths.autoResize !== AutoResizeColumns.Never;
                if (needAutoResize) {
                    _this.resizeColumns();
                }
                else {
                    var containerWidth = _this.getContainerWidth();
                    domel(_this.bodyCellContainerDiv)
                        .setStyle('width', containerWidth + "px");
                }
                return rowsToRender;
            })
                .catch(function (error) { console.error(error); return 0; });
        }
        this.bodyViewportDiv.addEventListener('scroll', function (ev) {
            domel(_this.headerViewportDiv)
                .setStyle('margin-left', "-" + _this.bodyViewportDiv.scrollLeft + "px");
        });
        this.bodyViewportDiv.addEventListener('keydown', this.onViewportKeydown.bind(this));
    };
    EasyGrid.prototype.isLastPage = function () {
        if (this.dataTable.elasticChunks) {
            return this.dataTable.totalIsKnown()
                && (this.pagination.page * this.pagination.pageSize) >= this.pagination.total;
        }
        return this.pagination.page * this.pagination.pageSize >= this.pagination.total;
    };
    EasyGrid.prototype.canShowAggregates = function () {
        if (!this.options || !this.options.aggregates || !this.options.aggregates.settings)
            return false;
        var aggrSettings = this.options.aggregates.settings;
        var result = (aggrSettings.hasAggregates() || aggrSettings.hasRecordCount())
            && (aggrSettings.hasGroups() || aggrSettings.hasGrandTotals());
        return result;
    };
    EasyGrid.prototype.updateTotalsState = function (groups, newRow, isLast) {
        if (isLast === void 0) { isLast = false; }
        var aggrSettings = this.options.aggregates.settings;
        if (this.prevRowTotals && aggrSettings.hasGroups()) {
            var changeLevel = -1;
            for (var level = 1; level <= groups.length; level++) {
                var group = groups[level - 1];
                for (var _i = 0, _a = group.columns; _i < _a.length; _i++) {
                    var col = _a[_i];
                    if (!aggrSettings.compareValues(this.prevRowTotals.getValue(col), newRow.getValue(col))) {
                        changeLevel = level;
                        break;
                    }
                }
                if (changeLevel !== -1)
                    break;
            }
            if (changeLevel !== -1) {
                for (var level = groups.length; level >= changeLevel; level--) {
                    var row = new DataRow(this.dataTable.columns, this.prevRowTotals.toArray());
                    var tr = this.renderTotalsRow(level, row);
                    this.bodyCellContainerDiv.appendChild(tr);
                }
            }
        }
        if (isLast && aggrSettings.hasGrandTotals() && aggrSettings.hasAggregates()) {
            var tr = this.renderTotalsRow(0, newRow);
            this.bodyCellContainerDiv.appendChild(tr);
        }
        this.prevRowTotals = newRow;
    };
    EasyGrid.prototype.applyGroupColumnTemplate = function (template, value, count) {
        var result = template.replace(/{{\s*GroupValue\s*}}/g, value ? "" + value : '-');
        result = result.replace(/{{\s*GroupCount\s*}}/g, count ? "" + count : '-');
        return result;
    };
    EasyGrid.prototype.renderTotalsRow = function (level, row) {
        var _this = this;
        var aggrSettings = this.options.aggregates.settings;
        var group = (level > 0)
            ? aggrSettings.getGroups()[level - 1]
            : { columns: [], aggregates: aggrSettings.getAggregates() };
        var rowBuilder = domel('div')
            .addClass(this.cssPrefix + "-row")
            .addClass(this.cssPrefix + "-row-totals")
            .addClass(this.cssPrefix + "-totals-lv" + level)
            .data('totals-level', "" + level)
            .attr('tabindex', '-1');
        var rowElement = rowBuilder.toDOM();
        this.columns.getItems().forEach(function (column, index) {
            if (!column.isVisible) {
                return;
            }
            var val = '';
            var colIndex = !column.isRowNum
                ? _this.dataTable.columns.getIndex(column.dataColumn.id)
                : -1;
            if (!column.isRowNum && column.dataColumn) {
                if (group.columns.indexOf(column.dataColumn.id) >= 0) {
                    val = row.getValue(colIndex);
                }
                ;
            }
            if (colIndex == _this.dataTable.columns.count - 1) {
                val = '.  .  .  .  .  .';
            }
            rowElement.appendChild(_this.renderCell(column, index, val, rowElement));
        });
        var aggrContainer = this.options.aggregates.calculator.getAggrContainer();
        var aggrCols = aggrSettings.getAggregates().map(function (c) { return c.colId; });
        var key = aggrSettings.buildGroupKey(group, row);
        aggrContainer.getAggregateData(level, key)
            .then(function (values) {
            for (var _i = 0, aggrCols_1 = aggrCols; _i < aggrCols_1.length; _i++) {
                var aggrColId = aggrCols_1[_i];
                row.setValue(aggrColId, values[aggrColId]);
            }
            rowElement.innerHTML = '';
            _this.columns.getItems().forEach(function (column, index) {
                if (!column.isVisible) {
                    return;
                }
                var val = '';
                var colIndex = !column.isRowNum
                    ? _this.dataTable.columns.getIndex(column.dataColumn.id)
                    : -1;
                if (!column.isRowNum) {
                    var isLastGroupColumn = false;
                    if (column.dataColumn) {
                        var groupColIndex = group.columns.indexOf(column.dataColumn.id);
                        var aggrColIndex = aggrCols.indexOf(column.dataColumn.id);
                        if (level > 0) {
                            isLastGroupColumn = groupColIndex == group.columns.length - 1;
                        }
                        else {
                            //if it's a grand total row consider first column as the last group column
                            isLastGroupColumn = colIndex == 0;
                        }
                        if (groupColIndex >= 0 || aggrColIndex >= 0) {
                            val = row.getValue(colIndex);
                        }
                        ;
                    }
                    var groupFooterTemplate = '';
                    if (level > 0) {
                        groupFooterTemplate = column.dataColumn.groupFooterColumnTemplate;
                        //set the default template for the last grouping column
                        if (!groupFooterTemplate && aggrSettings.hasRecordCount() && isLastGroupColumn) {
                            groupFooterTemplate = '{{GroupValue}} ({{GroupCount}})';
                        }
                    }
                    if (groupFooterTemplate) {
                        var cellDiv_1 = _this.renderCell(column, index, val, rowElement);
                        var innerCell = cellDiv_1.firstChild;
                        val = innerCell.innerHTML;
                        val = _this.applyGroupColumnTemplate(groupFooterTemplate, val, values[aggrSettings.COUNT_FIELD_NAME]);
                    }
                }
                var cellDiv = _this.renderCell(column, index, val, rowElement);
                rowElement.appendChild(cellDiv);
            });
        })
            .catch(function (error) { return console.error(error); });
        return rowElement;
    };
    EasyGrid.prototype.onViewportKeydown = function (ev) {
        if (this.options.showActiveRow) {
            var rowCount = this.bodyCellContainerDiv.querySelectorAll("." + this.cssPrefix + "-row").length;
            var newValue = void 0;
            switch (ev.key) {
                case 'ArrowLeft':
                    break;
                case 'ArrowRight':
                    break;
                case 'ArrowUp':
                    ev.preventDefault();
                    newValue = this.activeRowIndex < 0 || this.activeRowIndex >= rowCount ? rowCount - 1 : this.activeRowIndex - 1;
                    this.activeRowIndex = newValue >= 0 ? newValue : 0;
                    break;
                case 'ArrowDown':
                    ev.preventDefault();
                    newValue = this.activeRowIndex < 0 || this.activeRowIndex >= rowCount ? 0 : this.activeRowIndex + 1;
                    this.activeRowIndex = newValue < rowCount ? newValue : rowCount - 1;
                    break;
            }
        }
    };
    EasyGrid.prototype.ensureRowVisibility = function (rowOrIndex) {
        var row = typeof rowOrIndex === 'number'
            ? this.getDataRow(rowOrIndex)
            : rowOrIndex;
        if (row) {
            var rowRect = row.getBoundingClientRect();
            var viewportRect = this.bodyViewportDiv.getBoundingClientRect();
            var rowTop = rowRect.top - viewportRect.top;
            var rowBottom = rowRect.bottom - viewportRect.top;
            var viewportHeight = this.bodyViewportDiv.clientHeight;
            var windowHeight = window.innerHeight || document.documentElement.clientHeight;
            if (rowTop > 0 &&
                rowBottom <= viewportHeight &&
                rowRect.top > 0 &&
                rowRect.bottom < windowHeight) {
                return;
            }
            if (rowTop < 0) {
                this.bodyViewportDiv.scrollTop = this.bodyViewportDiv.scrollTop + rowTop;
            }
            else if (rowBottom > viewportHeight) {
                this.bodyViewportDiv.scrollTop = this.bodyViewportDiv.scrollTop + rowBottom - viewportHeight;
            }
            rowRect = row.getBoundingClientRect();
            if (rowRect.top < 0) {
                document.documentElement.scrollTop = document.documentElement.scrollTop + rowRect.top;
            }
            else if (rowRect.bottom > windowHeight) {
                document.documentElement.scrollTop = document.documentElement.scrollTop + rowRect.bottom - windowHeight;
            }
        }
    };
    /** Returns a promise with the list of the rows to render on one page.
     * The list contains pageSize+1 row to make it possible
     * to render the totals row (if it appears to be on the edge between pages)
     */
    EasyGrid.prototype.getRowsToRender = function () {
        if (this.options.paging.enabled === false) {
            return Promise.resolve(this.dataTable.getCachedRows());
        }
        return this.dataTable.getRows({
            offset: (this.pagination.page - 1) * this.pagination.pageSize,
            limit: this.pagination.pageSize + 1
        })
            .catch(function (error) {
            console.error(error);
            return [];
        });
    };
    EasyGrid.prototype.renderFooter = function () {
        var _this = this;
        this.footerDiv = domel('div')
            .addClass(this.cssPrefix + "-footer")
            .toDOM();
        if (this.rowsOnPagePromise) {
            this.rowsOnPagePromise.then(function (count) {
                _this.footerDiv.innerHTML = '';
                _this.footerPaginateDiv = _this.renderPageNavigator();
                _this.footerDiv.appendChild(_this.footerPaginateDiv);
                var pageInfoBlock = _this.renderPageInfoBlock(count);
                _this.footerDiv.appendChild(pageInfoBlock);
            });
        }
    };
    EasyGrid.prototype.renderPageInfoBlock = function (count) {
        var pageInfoDiv = domel('div')
            .addClass(this.cssPrefix + "-page-info")
            .toDOM();
        var rowCount = this.dataTable.getTotal();
        if (rowCount > 0) {
            var fistPageRecordNum = count
                ? (this.pagination.page - 1) * this.pagination.pageSize + 1
                : 0;
            var lastPageRecordNum = count
                ? fistPageRecordNum + count - 1
                : 0;
            var totalStr = this.dataTable.getTotal().toString();
            if (this.dataTable.elasticChunks) {
                var count_1 = this.dataTable.getCachedCount();
                var total = this.dataTable.getTotal();
                if (count_1 !== total)
                    totalStr = '?';
            }
            pageInfoDiv.innerHTML = i18n.getText('GridPageInfo')
                .replace('{FirstPageRecordNum}', "<span>" + fistPageRecordNum.toString() + "</span>")
                .replace('{LastPageRecordNum}', "<span>" + lastPageRecordNum.toString() + "</span>")
                .replace('{Total}', "<span>" + totalStr + "</span>");
        }
        return pageInfoDiv;
    };
    EasyGrid.prototype.showProgress = function () {
    };
    EasyGrid.prototype.hideProgress = function () {
    };
    EasyGrid.prototype.getLocalIndexByGlobal = function (index) {
        if (this.pagination) {
            return index % this.pagination.pageSize;
        }
        else {
            return index;
        }
    };
    EasyGrid.prototype.getGlobalIndexByLocal = function (index) {
        if (this.pagination) {
            return (this.pagination.page - 1) * this.pagination.pageSize + index;
        }
        else {
            return index;
        }
    };
    EasyGrid.prototype.renderRow = function (row, index) {
        var _this = this;
        var indexGlobal = this.getGlobalIndexByLocal(index);
        var rowBuilder = domel('div')
            .addClass(this.cssPrefix + "-row")
            .addClass(this.cssPrefix + "-row-" + (index % 2 == 1 ? 'odd' : 'even'))
            .data('row-idx', "" + indexGlobal)
            .attr('tabindex', '-1')
            .on('click', function (ev) {
            _this.activeRowIndex = index;
            _this.fireEvent({
                type: 'rowClick',
                row: row,
                rowIndex: index,
                sourceEvent: ev
            });
        })
            .on('dblclick', function (ev) {
            _this.fireEvent({
                type: 'rowDbClick',
                row: row,
                rowIndex: index,
                sourceEvent: ev
            });
        });
        if (index == 0) {
            rowBuilder.addClass(this.cssPrefix + "-row-first");
        }
        var rowElement = rowBuilder.toDOM();
        if (this.options.showActiveRow && index == this.activeRowIndex) {
            rowBuilder.addClass(this.cssPrefix + "-row-active");
        }
        this.columns.getItems().forEach(function (column, index) {
            if (!column.isVisible) {
                return;
            }
            var colindex = column.isRowNum ? -1 : _this.dataTable.columns.getIndex(column.dataColumn.id);
            var val = column.isRowNum ? indexGlobal + 1 : row.getValue(colindex);
            rowElement.appendChild(_this.renderCell(column, index, val, rowElement));
        });
        return rowElement;
    };
    EasyGrid.prototype.renderCell = function (column, colIndex, value, rowElement) {
        var builder = domel('div')
            .addClass(this.cssPrefix + "-cell")
            .data('col-idx', "" + colIndex)
            .attr('tabindex', '-1')
            .setStyle('width', column.width + "px");
        if (column.align == GridColumnAlign.LEFT) {
            builder.addClass(this.cssPrefix + "-cell-align-left");
        }
        else if (column.align == GridColumnAlign.RIGHT) {
            builder.addClass(this.cssPrefix + "-cell-align-right");
        }
        else if (column.align == GridColumnAlign.CENTER) {
            builder.addClass(this.cssPrefix + "-cell-align-center");
        }
        var cellElement = builder.toDOM();
        var valueCell = cellElement.appendChild(domel('div')
            .addClass(this.cssPrefix + "-cell-value")
            .toDOM());
        if (column.cellRenderer) {
            column.cellRenderer(value, column, valueCell, rowElement);
        }
        return cellElement;
    };
    /** Sets current grid pages (if paging is used) */
    EasyGrid.prototype.setPage = function (page) {
        this.pagination.page = page;
        this.fireEvent({ type: "pageChanged", page: page });
        this.refresh();
        this.bodyViewportDiv.focus();
    };
    EasyGrid.prototype.renderPageNavigator = function () {
        var _this = this;
        var paginateDiv = document.createElement('div');
        paginateDiv.className = this.cssPrefix + "-pagination-wrapper";
        var rowCount = this.dataTable.getTotal();
        if (this.options.paging && this.options.paging.enabled && rowCount > 0) {
            var prefix_1 = this.paginationOptions.useBootstrap ? '' : this.cssPrefix + "-";
            var buttonClickHandler_1 = function (ev) {
                var element = ev.target;
                if (element.hasAttribute('data-page')) {
                    var page = parseInt(element.getAttribute('data-page'));
                    _this.setPage(page);
                }
            };
            var renderPageCell = function (pageIndex, content, disabled, extreme, active) {
                var li = document.createElement('li');
                li.className = prefix_1 + "page-item";
                if (!extreme) {
                    if (active) {
                        li.className += ' active';
                    }
                    var a_1 = document.createElement('a');
                    a_1.setAttribute('href', 'javascript:void(0)');
                    a_1.innerHTML = content || pageIndex.toString();
                    a_1.setAttribute("data-page", "" + pageIndex);
                    a_1.className = prefix_1 + "page-link";
                    a_1.addEventListener("click", buttonClickHandler_1);
                    li.appendChild(a_1);
                    return li;
                }
                var a = document.createElement('span');
                a.setAttribute('aria-hidden', 'true');
                a.className = prefix_1 + "page-link";
                if (disabled) {
                    li.className += ' disabled';
                }
                else {
                    if (_this.paginationOptions.useBootstrap) {
                        a = document.createElement('a');
                        a.setAttribute('href', 'javascript:void(0)');
                        a.setAttribute("data-page", "" + pageIndex);
                    }
                    else {
                        var newA = document.createElement('a');
                        newA.setAttribute('href', 'javascript:void(0)');
                        newA.setAttribute('data-page', "" + pageIndex);
                        a = newA;
                    }
                    a.className = prefix_1 + "page-link";
                    a.addEventListener("click", buttonClickHandler_1);
                }
                a.innerHTML = content;
                li.appendChild(a);
                return li;
            };
            if (this.dataTable.elasticChunks) {
                var pageIndex = this.pagination.page || 1;
                var ul = document.createElement('ul');
                ul.className = prefix_1 + "pagination";
                var cell = renderPageCell(pageIndex - 1, '&laquo;', pageIndex == 1, true, false);
                ul.appendChild(cell);
                cell = renderPageCell(pageIndex + 1, '&raquo;', this.isLastPage(), true, false);
                ul.appendChild(cell);
                paginateDiv.appendChild(ul);
            }
            else {
                if (this.pagination.total > this.pagination.pageSize) {
                    var pageIndex = this.pagination.page || 1;
                    var pageCount = Math.ceil(this.pagination.total / this.pagination.pageSize) || 1;
                    var maxButtonCount = this.paginationOptions.maxButtonCount || 10;
                    var zeroBasedIndex = pageIndex - 1;
                    var firstPageIndex = zeroBasedIndex - (zeroBasedIndex % maxButtonCount) + 1;
                    var lastPageIndex = firstPageIndex + maxButtonCount - 1;
                    if (lastPageIndex > pageCount) {
                        lastPageIndex = pageCount;
                    }
                    var ul = document.createElement('ul');
                    ul.className = prefix_1 + "pagination";
                    var cell = renderPageCell(firstPageIndex - 1, '&laquo;', firstPageIndex == 1, true, false);
                    ul.appendChild(cell);
                    for (var i = firstPageIndex; i <= lastPageIndex; i++) {
                        cell = renderPageCell(i, i.toString(), false, false, i == pageIndex);
                        ul.appendChild(cell);
                    }
                    cell = renderPageCell(lastPageIndex + 1, '&raquo;', lastPageIndex == pageCount, true, false);
                    ul.appendChild(cell);
                    paginateDiv.appendChild(ul);
                }
            }
            if (this.options.paging.allowPageSizeChange) {
                var selectChangeHandler = function (ev) {
                    var newValue = parseInt(ev.target.value);
                    _this.pagination.pageSize = newValue;
                    _this.pagination.page = 1;
                    _this.refresh();
                };
                var pageSizes = document.createElement('div');
                pageSizes.className = this.cssPrefix + "-page-sizes";
                var selectSize = document.createElement('div');
                selectSize.className = "kfrm-select " + this.cssPrefix + "-page-sizes-select";
                pageSizes.appendChild(selectSize);
                var sel_1 = document.createElement('select');
                var selOptions = this.options.paging.pageSizeItems || [];
                var selSet = new Set(selOptions);
                selSet.add(this.options.paging.pageSize || 20);
                Array.from(selSet).forEach(function (el) {
                    var option = document.createElement("option");
                    option.value = el.toString();
                    option.text = el.toString();
                    sel_1.appendChild(option);
                });
                sel_1.value = (this.pagination.pageSize || 20).toString();
                selectSize.appendChild(sel_1);
                sel_1.addEventListener('change', selectChangeHandler);
                var labelDiv = document.createElement('div');
                labelDiv.className = this.cssPrefix + "-page-sizes-label";
                pageSizes.appendChild(labelDiv);
                var label = document.createElement('span');
                label.innerText = i18n.getText('GridItemsPerPage');
                labelDiv.appendChild(label);
                paginateDiv.appendChild(pageSizes);
            }
        }
        return paginateDiv;
    };
    EasyGrid.prototype.addEventListener = function (eventType, handler) {
        return this.eventEmitter.subscribe(eventType, function (event) { return handler(event.data); });
    };
    EasyGrid.prototype.removeEventListener = function (eventType, handlerId) {
        this.eventEmitter.unsubscribe(eventType, handlerId);
    };
    EasyGrid.prototype.renderHeaderButtons = function () {
        var _this = this;
        if (this.options.showPlusButton) {
            return domel('div')
                .addClass(this.cssPrefix + "-header-btn-plus")
                .title(this.options.plusButtonTitle || 'Add')
                .addChild('a', function (builder) { return builder
                .attr('href', 'javascript:void(0)')
                .on('click', function (e) {
                e.preventDefault();
                _this.fireEvent({
                    type: 'plusButtonClick',
                    sourceEvent: e
                });
            }); })
                .toDOM();
        }
        return domel('span')
            .addText('#')
            .toDOM();
    };
    EasyGrid.prototype.showLandingSlot = function (pageX, pageY) {
        var colElems = this.headerRowDiv.querySelectorAll("[class*=" + this.cssPrefix + "-table-col]");
        var cols = [];
        for (var i = 1; i < colElems.length; i++) {
            var rowElem = colElems[i];
            if (rowElem.style.display === 'none')
                continue;
            cols.push(rowElem);
        }
        if (cols.length === 0) {
            this.landingIndex = 0;
            this.headerRowDiv.appendChild(this.landingSlot);
            return;
        }
        var landingPos = getElementAbsolutePos(this.landingSlot);
        if (pageX >= landingPos.x && pageX <= landingPos.x + this.landingSlot.offsetWidth) {
            return;
        }
        var newLandingIndex = this.landingIndex;
        for (var _i = 0, cols_1 = cols; _i < cols_1.length; _i++) {
            var col = cols_1[_i];
            var colPos = getElementAbsolutePos(col);
            var width = col.offsetWidth;
            if (pageX > colPos.x && pageX < colPos.x + width) {
                // -1 as we don't need to count add button here
                newLandingIndex = parseInt(col.getAttribute('data-col-idx')) - 1;
            }
        }
        if (newLandingIndex != this.landingIndex) {
            this.landingIndex = newLandingIndex;
            if (this.landingIndex < cols.length) {
                this.headerRowDiv.insertBefore(this.landingSlot, cols[this.landingIndex]);
            }
            else {
                this.headerRowDiv.appendChild(this.landingSlot);
            }
        }
    };
    EasyGrid.prototype.hideLandingSlot = function () {
        var _this = this;
        this.landingIndex = -1;
        setTimeout(function () {
            if (_this.landingSlot.parentElement) {
                _this.landingSlot.parentElement.removeChild(_this.landingSlot);
            }
        }, 10);
    };
    Object.defineProperty(EasyGrid.prototype, "activeRowIndex", {
        get: function () {
            return this._activeRowIndex;
        },
        set: function (value) {
            if (value !== this._activeRowIndex) {
                var oldValue = this._activeRowIndex;
                this._activeRowIndex = value;
                this.updateActiveRow();
                this.fireEvent({
                    type: 'activeRowChanged',
                    oldValue: oldValue,
                    newValue: this.activeRowIndex,
                    rowIndex: this.getGlobalIndexByLocal(this.activeRowIndex)
                });
            }
        },
        enumerable: true,
        configurable: true
    });
    EasyGrid.prototype.updateActiveRow = function () {
        var _this = this;
        if (this.options.showActiveRow) {
            var rows = this.bodyCellContainerDiv.querySelectorAll("[class*=" + this.cssPrefix + "-row-active]");
            rows.forEach(function (el) { el.classList.remove(_this.cssPrefix + "-row-active"); });
            var activeRow = this.getActiveRow();
            if (activeRow) {
                activeRow.classList.add(this.cssPrefix + "-row-active");
                this.ensureRowVisibility(this.activeRowIndex);
            }
        }
    };
    EasyGrid.prototype.getActiveRow = function () {
        return this.getDataRow(this.activeRowIndex);
    };
    EasyGrid.prototype.getDataRow = function (index) {
        var rows = Array.from(this.bodyCellContainerDiv.querySelectorAll("." + this.cssPrefix + "-row:not(." + this.cssPrefix + "-row-totals)"));
        if (index >= 0 && index < rows.length)
            return rows[index];
        return null;
    };
    /** Makes the grid focused for keyboard events */
    EasyGrid.prototype.focus = function () {
        this.bodyViewportDiv.focus();
    };
    /** Resizes columns according to the data they represent */
    EasyGrid.prototype.resizeColumns = function () {
        if (this.options.columnWidths.autoResize === AutoResizeColumns.Never)
            return;
        var containerWidth = this.bodyCellContainerDiv.style.width;
        this.bodyCellContainerDiv.style.visibility = 'hidden';
        this.bodyCellContainerDiv.style.width = '1px';
        //this.headerRowDiv.style.visibility = 'hidden';
        this.headerRowDiv.style.width = '1px';
        var sumWidth = 0;
        var columns = this.columns.getItems();
        var headerCells = this.headerCellContainerDiv.querySelectorAll("." + this.cssPrefix + "-header-cell");
        var headerIdx = 0;
        var _loop_1 = function (idx) {
            var column = columns[idx];
            if (!column.isVisible)
                return "continue";
            var calculatedWidth = this_1.options.columnWidths.autoResize !== AutoResizeColumns.Always && column.dataColumn
                ? column.dataColumn.calculatedWidth
                : 0;
            var cellValues = this_1.bodyCellContainerDiv.querySelectorAll("." + this_1.cssPrefix + "-cell[data-col-idx=\"" + idx + "\"] > ." + this_1.cssPrefix + "-cell-value");
            var maxWidth = 0;
            if (calculatedWidth > 0) {
                sumWidth += calculatedWidth;
                column.width = calculatedWidth;
                cellValues.forEach(function (value) {
                    value.parentElement.style.width = calculatedWidth + "px";
                });
                headerCells[headerIdx].style.width = calculatedWidth + "px";
            }
            else {
                if (cellValues.length == 0) {
                    headerCells[headerIdx].style.width = null;
                    headerCells[headerIdx].style.whiteSpace = 'nowrap';
                }
                maxWidth = headerCells[headerIdx].offsetWidth;
                if (cellValues.length > 0) {
                    cellValues.forEach(function (value) {
                        value.parentElement.style.width = null;
                        var width = value.parentElement.offsetWidth;
                        if (width > maxWidth) {
                            maxWidth = width;
                        }
                    });
                    maxWidth += 3;
                    var maxOption = column.isRowNum
                        ? this_1.options.columnWidths.rowNumColumn.max || 500
                        : this_1.options.columnWidths[column.dataColumn.type].max || 2000;
                    var minOption = column.isRowNum
                        ? this_1.options.columnWidths.rowNumColumn.min || 0
                        : this_1.options.columnWidths[column.dataColumn.type].min || 20;
                    if (maxWidth > maxOption) {
                        maxWidth = maxOption;
                    }
                    if (maxWidth < minOption) {
                        maxWidth = minOption;
                    }
                    if (utils.isNumericType(column.type)) {
                        //increase the calculated width in 1.3 times for numeric columns
                        maxWidth = Math.round(maxWidth * 1.3);
                    }
                    sumWidth += maxWidth;
                    column.width = maxWidth;
                    cellValues.forEach(function (value) {
                        value.parentElement.style.width = maxWidth + "px";
                    });
                    headerCells[headerIdx].style.width = maxWidth + "px";
                    if (column.dataColumn) {
                        column.dataColumn.calculatedWidth = maxWidth;
                    }
                }
                else {
                    sumWidth += maxWidth;
                }
            }
            headerIdx++;
        };
        var this_1 = this;
        for (var idx = 0; idx < this.columns.count; idx++) {
            _loop_1(idx);
        }
        if (sumWidth > 0) {
            this.bodyCellContainerDiv.style.width = sumWidth + "px";
            this.headerCellContainerDiv.style.width = sumWidth + "px";
        }
        else {
            this.bodyCellContainerDiv.style.width = containerWidth;
            this.headerCellContainerDiv.style.width = containerWidth;
        }
        this.bodyCellContainerDiv.style.visibility = null;
        this.headerRowDiv.removeAttribute('style');
    };
    return EasyGrid;
}());
export { EasyGrid };
//# sourceMappingURL=easy_grid.js.map